import { preparationHtmlToTipTap, telegramHtmlToTipTap } from '../rich-text/tiptap-html.js';
import { computeTelegramVisibleLength } from './tiptap-visible-length.js';
import type { TipTapDocument } from './tiptap-to-telegram-html.js';

export const BOOKING_FIRST_NAME_LENGTH = 25;
export const BOOKING_NOTE_LENGTH = 800;
export const BOOKING_PREPARATION_LENGTH = 4096;

/** The editor and every save door use the same post-serialization metric. */
export function countBookingMessageDocument(doc: TipTapDocument): number {
  return computeTelegramVisibleLength(doc, { firstNameTokenLength: BOOKING_FIRST_NAME_LENGTH });
}

export class BookingMessageContentError extends Error {
  readonly statusCode = 400;
  readonly code = 'BOOKING_MESSAGE_CONTENT_TOO_LONG';
  constructor(readonly field: string, readonly limit: number, readonly actual: number) {
    super(`${field} must be ${limit} visible characters or fewer (received ${actual})`);
  }
}

export interface BookingMessageContentInput {
  preparationNotes?: string | null;
  reminders?: ReadonlyArray<{ message?: string | null }>;
}

/** Validate supplied values only. Copied or unchanged legacy values are not a PATCH input. */
export function assertBookingMessageContent(input: BookingMessageContentInput | undefined, prefix = ''): void {
  if (!input) return;
  if (input.preparationNotes != null) {
    const count = countBookingMessageDocument(preparationHtmlToTipTap(input.preparationNotes));
    if (count > BOOKING_PREPARATION_LENGTH) {
      throw new BookingMessageContentError(`${prefix}preparationNotes`, BOOKING_PREPARATION_LENGTH, count);
    }
  }
  input.reminders?.forEach((reminder, index) => {
    const count = countBookingMessageDocument(telegramHtmlToTipTap(reminder.message));
    if (count > BOOKING_NOTE_LENGTH) {
      throw new BookingMessageContentError(`${prefix}reminders[${index}].message`, BOOKING_NOTE_LENGTH, count);
    }
  });
}

/** Bound displayed facts without splitting a UTF-16 surrogate pair. Includes the ellipsis. */
export function boundBookingFact(value: string, limit: number): string {
  if (value.length <= limit) return value;
  const prefix = value.slice(0, Math.max(0, limit - 1)).replace(/[\uD800-\uDBFF]$/, '');
  return `${prefix}…`;
}

/** Plain-text only. Preserve every character, including whitespace at page boundaries. */
export function splitPreparationText(text: string, firstPageAllowance: number, pageAllowance = 4096): string[] {
  if (firstPageAllowance < 2 || pageAllowance < 2) throw new RangeError('Preparation page allowance must fit a code point');
  const pages: string[] = [];
  let remaining = text;
  while (remaining.length) {
    const allowance = pages.length === 0 ? firstPageAllowance : pageAllowance;
    if (remaining.length <= allowance) { pages.push(remaining); break; }
    let cut = allowance;
    // Include the separator on the preceding page, rather than trimming it away.
    for (const separator of ['\n\n', '\n', ' ']) {
      const at = remaining.lastIndexOf(separator, allowance - separator.length);
      if (at > 0) { cut = at + separator.length; break; }
    }
    if (/[\uD800-\uDBFF]/.test(remaining[cut - 1]) && /[\uDC00-\uDFFF]/.test(remaining[cut])) cut--;
    pages.push(remaining.slice(0, cut));
    remaining = remaining.slice(cut);
  }
  return pages;
}
